import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Avatar, Stack, FormControlLabel, Checkbox } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  MedicalServices as MedicalIcon,
  Cancel as CancelIcon,
  LocalPharmacy as DispenseIcon,
  PostAdd as PostAddIcon,
} from '@mui/icons-material';
import PrescriptionView from './PrescriptionView';
import { formatDateOnly } from '@utils/dateFormatter';
import { prescriptionsService, type StaffPrescriptionRow } from '@services/prescriptionsService';
import { useAuth } from '@contexts/AuthContext';

interface PrescriptionItem {
  itemId?: string;
  productId: string;
  productName: string;
  dose: number;
  unit: string;
  frequency: string;
  durationDays: number;
  qty: number;
  qtyDispensed: number;
  route: string;
  notes?: string;
}

interface Prescription {
  id: string;
  prescriptionNumber: string;
  visitId?: string;
  patientId: string;
  patientName: string;
  patientSpecies: string;
  patientWeight: number;
  clientName: string;
  doctorId: string;
  doctorName: string;
  displayStatus?: string;
  items: PrescriptionItem[];
  status: 'draft' | 'active' | 'signed' | 'cancelled';
  createdAt: string;
  signedAt?: string;
  dispensedAt?: string;
  revokedAt?: string;
  validUntil: string;
  notes?: string;
  instructions?: string;
  useInternalStock: boolean;
}

const PrescriptionsManagementNew: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<'date' | 'number'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Prescription | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [viewDetail, setViewDetail] = useState<StaffPrescriptionRow | null>(null);
  const [addendumOpen, setAddendumOpen] = useState(false);
  const [addendumRxId, setAddendumRxId] = useState<string | null>(null);
  const [addendumText, setAddendumText] = useState('');
  const [addendumClientVisible, setAddendumClientVisible] = useState(true);

  const loadPrescriptions = async () => {
    setLoading(true);

    try {
      const needsAll =
        selectedStatus === 'all' ||
        selectedStatus === 'issued' ||
        selectedStatus === 'partially_dispensed' ||
        selectedStatus === 'dispensed' ||
        selectedStatus === 'expired';

      const statusParam =
        needsAll
          ? undefined
          : selectedStatus === 'cancelled'
            ? 'cancelled'
            : selectedStatus === 'draft'
              ? 'draft'
              : selectedStatus === 'active' || selectedStatus === 'signed'
                ? 'active'
                : undefined;

      const prescriptionsResponse = await prescriptionsService.getPrescriptions(
        statusParam ? { status: statusParam } : {}
      );

      const mapped: Prescription[] = prescriptionsResponse.map((p) => ({
        id: p.id,
        prescriptionNumber: `RX-${p.id.slice(0, 8).toUpperCase()}`,
        visitId: p.visit_id || undefined,
        patientId: p.patient_id,
        patientName: p.patient?.name || 'Pacient necunoscut',
        patientSpecies: p.patient?.species || 'N/A',
        patientWeight: 0,
        clientName: p.owner_name || '—',
        doctorId: p.doctor_id,
        doctorName: p.veterinarian_name?.trim() || 'Medic',
        displayStatus: p.display_status,
        items: (p.items || []).map((item: any) => ({
          itemId: item.id,
          productId: item.product_id || '',
          productName: item.medication_name || 'Medicament',
          dose: Number(item.dosage) || 0,
          unit: item.unit || '',
          frequency: item.frequency || '',
          durationDays: Number(item.duration) || 0,
          qty: Number(item.quantity_prescribed ?? item.quantity) || 0,
          qtyDispensed: Number(item.quantity_dispensed) || 0,
          route: '',
          notes: item.instructions || '',
        })),
        status: p.status === 'cancelled' ? 'cancelled' : (p.status as any),
        createdAt: p.created_at,
        signedAt: undefined,
        validUntil: p.created_at,
        notes: p.notes,
        instructions: undefined,
        useInternalStock: false,
      }));

      setPrescriptions(mapped);
    } catch (error) {
      console.error('Eroare la incarcarea retetelor:', error);
      setPrescriptions([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadPrescriptions();
  }, [selectedStatus]);

  const getStatusColor = (prescription: Prescription) => {
    const d = prescription.displayStatus || '';
    if (d === 'revoked' || prescription.status === 'cancelled') return 'error';
    if (d === 'expired') return 'warning';
    if (d === 'dispensed') return 'success';
    if (d === 'partially_dispensed') return 'info';
    if (d === 'issued') return 'primary';
    switch (prescription.status) {
      case 'draft': return 'default';
      case 'active': return 'primary';
      case 'signed': return 'primary';
      default: return 'default';
    }
  };

  const getStatusLabel = (prescription: Prescription) => {
    const d = prescription.displayStatus;
    if (d === 'issued') return 'Emisă';
    if (d === 'partially_dispensed') return 'Parțial eliberată';
    if (d === 'dispensed') return 'Eliberată';
    if (d === 'revoked') return 'Revocată';
    if (d === 'expired') return 'Expirată';
    if (prescription.status === 'cancelled') return 'Anulată';
    switch (prescription.status) {
      case 'draft': return 'Draft';
      case 'active': return 'Activa';
      case 'signed': return 'Semnată';
      default: return prescription.status;
    }
  };

  const role = user?.role || '';
  const canIssueRevoke = ['veterinarian', 'clinic_admin', 'master_admin'].includes(role);
  const canDispense = ['veterinarian', 'clinic_admin', 'reception', 'master_admin'].includes(role);

  const openRevoke = (prescription: Prescription) => {
    setRevokeTarget(prescription);
    setRevokeReason('');
    setActionError(null);
    setRevokeOpen(true);
  };

  const submitRevoke = async () => {
    if (!revokeTarget) return;
    const r = revokeReason.trim();
    if (r.length < 3) {
      setActionError('Motivul trebuie să aibă minim 3 caractere');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      await prescriptionsService.revokePrescription(revokeTarget.id, r);
      setRevokeOpen(false);
      await loadPrescriptions();
    } catch (e: any) {
      setActionError(e?.message || 'Anulare eșuată');
    } finally {
      setActionLoading(false);
    }
  };

  const dispenseRemaining = async (prescription: Prescription) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const detail = await prescriptionsService.getPrescription(prescription.id);
      const lines = (detail.items || [])
        .map((it) => {
          const pres = Number(it.quantity_prescribed) || 0;
          const disp = Number(it.quantity_dispensed) || 0;
          const q = pres - disp;
          if (q <= 1e-6) return null;
          return { prescription_item_id: it.id, quantity: q };
        })
        .filter(Boolean) as Array<{ prescription_item_id: string; quantity: number }>;
      if (!lines.length) {
        setActionError('Nu există cantitate de eliberat');
        return;
      }
      await prescriptionsService.dispense(prescription.id, lines);
      await loadPrescriptions();
    } catch (e: any) {
      setActionError(e?.message || 'Eliberare eșuată');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewPrescription = async (prescription: Prescription) => {
    setSelectedPrescription(prescription);
    setViewDetail(null);
    setViewDialogOpen(true);
    try {
      const d = await prescriptionsService.getPrescription(prescription.id);
      setViewDetail(d);
    } catch {
      setViewDetail(null);
    }
  };

  const openAddendum = (prescription: Prescription) => {
    setAddendumRxId(prescription.id);
    setAddendumText('');
    setAddendumClientVisible(true);
    setActionError(null);
    setAddendumOpen(true);
  };

  const submitAddendum = async () => {
    if (!addendumRxId) return;
    const t = addendumText.trim();
    if (t.length < 1) {
      setActionError('Textul addendumului este obligatoriu');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      await prescriptionsService.addAddendum(addendumRxId, {
        text: t,
        client_visible: addendumClientVisible,
      });
      setAddendumOpen(false);
      await loadPrescriptions();
      if (viewDialogOpen && selectedPrescription?.id === addendumRxId) {
        const d = await prescriptionsService.getPrescription(addendumRxId);
        setViewDetail(d);
      }
    } catch (e: any) {
      setActionError(e?.message || 'Addendum eșuat');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintPrescription = async (prescription: Prescription) => {
    try {
      await prescriptionsService.generatePrescriptionPDF(prescription.id);
    } catch (error) {
      console.error('Eroare la printarea retetei:', error);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPrescription(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrare si sortare prescriptii
  const filteredPrescriptions = prescriptions
    .filter(prescription => {
      const matchesSearch = !searchTerm || 
        prescription.prescriptionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prescription.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prescription.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prescription.doctorName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDoctor = selectedDoctor === 'all' || prescription.doctorId === selectedDoctor;
      const d = prescription.displayStatus || '';
      const matchesStatus =
        selectedStatus === 'all' ||
        (selectedStatus === 'draft' && prescription.status === 'draft') ||
        (selectedStatus === 'cancelled' && prescription.status === 'cancelled') ||
        (selectedStatus === 'signed' && prescription.status === 'active') ||
        (selectedStatus === 'active' && prescription.status === 'active') ||
        (selectedStatus === 'issued' && d === 'issued') ||
        (selectedStatus === 'partially_dispensed' && d === 'partially_dispensed') ||
        (selectedStatus === 'dispensed' && d === 'dispensed') ||
        (selectedStatus === 'expired' && d === 'expired');
      
      // Filter by period
      const prescriptionDate = new Date(prescription.createdAt);
      const now = new Date();
      let matchesPeriod = true;
      
      if (selectedPeriod === 'current_month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        matchesPeriod = prescriptionDate >= startOfMonth;
      } else if (selectedPeriod === 'last_month') {
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        matchesPeriod = prescriptionDate >= startOfLastMonth && prescriptionDate <= endOfLastMonth;
      } else if (selectedPeriod === 'last_7_days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        matchesPeriod = prescriptionDate >= sevenDaysAgo;
      }
      
      return matchesSearch && matchesDoctor && matchesStatus && matchesPeriod;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortBy === 'number') {
        return sortOrder === 'asc' 
          ? a.prescriptionNumber.localeCompare(b.prescriptionNumber)
          : b.prescriptionNumber.localeCompare(a.prescriptionNumber);
      }
      return 0;
    });

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca prescriptiile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Retete & Prescriptii
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Prescriere si tiparire retete - iteme, doze, frecventa, durata, semnatura, istoric
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{ borderRadius: 2 }}
          onClick={() => navigate('/clinic/patients')}
        >
          Pacienți (emitere rețetă)
        </Button>
      </Box>

      {/* Filtre Sticky */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 2,
        position: 'sticky',
        top: 64,
        zIndex: 1100,
        backgroundColor: 'background.paper'
      }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Cauta numar reteta, pacient, client sau doctor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="active">Active (toate emise)</MenuItem>
                  <MenuItem value="issued">Emisă (fără eliberare)</MenuItem>
                  <MenuItem value="partially_dispensed">Parțial eliberată</MenuItem>
                  <MenuItem value="dispensed">Eliberată complet</MenuItem>
                  <MenuItem value="expired">Expirată</MenuItem>
                  <MenuItem value="signed">Semnate</MenuItem>
                  <MenuItem value="cancelled">Anulate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Perioada</InputLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  label="Perioada"
                >
                  <MenuItem value="current_month">Luna curenta</MenuItem>
                  <MenuItem value="last_month">Luna trecuta</MenuItem>
                  <MenuItem value="last_7_days">Ultimele 7 zile</MenuItem>
                  <MenuItem value="all">Toate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Doctor</InputLabel>
                <Select
                  value={selectedDoctor}
                  onChange={(e) => setSelectedDoctor(e.target.value)}
                  label="Doctor"
                >
                  {/* Lista doctori nu este disponibila in acest ecran; filtrarea este globala. */}
                  <MenuItem value="all">Toti</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                disabled
                sx={{ borderRadius: 2 }}
              >
                Filtre Avansate
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                fullWidth
                onClick={loadPrescriptions}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {actionError && !revokeOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Tabel */}
      <Card sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nr. Reteta</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Pacient</TableCell>
                <TableCell>Doctor</TableCell>
                <TableCell>Nr. Produse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrescriptions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((prescription) => (
                  <TableRow key={prescription.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {prescription.prescriptionNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDateOnly(prescription.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: prescription.status === 'cancelled' ? 'error.light' :
                                  prescription.status === 'signed' || prescription.status === 'active'
                                    ? 'primary.light'
                                    : 'default.light'
                        }}>
                          <MedicalIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {prescription.patientName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {prescription.patientSpecies} • {prescription.clientName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {prescription.doctorName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {prescription.items.length} produse
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(prescription)} 
                        color={getStatusColor(prescription) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Vezi">
                          <IconButton 
                            size="small"
                            onClick={() => handleViewPrescription(prescription)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Printeaza">
                          <IconButton 
                            size="small"
                            onClick={() => handlePrintPrescription(prescription)}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                        {canIssueRevoke &&
                          prescription.status !== 'cancelled' &&
                          prescription.displayStatus !== 'revoked' &&
                          prescription.displayStatus !== 'expired' &&
                          prescription.displayStatus !== 'dispensed' &&
                          prescription.displayStatus !== 'partially_dispensed' && (
                          <Tooltip title="Revocă prescripția">
                            <IconButton size="small" onClick={() => openRevoke(prescription)} disabled={actionLoading}>
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canIssueRevoke &&
                          (prescription.displayStatus === 'issued' ||
                            prescription.displayStatus === 'partially_dispensed') && (
                          <Tooltip title="Adaugă addendum">
                            <IconButton size="small" onClick={() => openAddendum(prescription)} disabled={actionLoading}>
                              <PostAddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDispense &&
                          prescription.displayStatus !== 'expired' &&
                          (prescription.displayStatus === 'issued' ||
                            prescription.displayStatus === 'partially_dispensed') && (
                          <Tooltip title="Eliberează restul cantităților">
                            <IconButton
                              size="small"
                              onClick={() => dispenseRemaining(prescription)}
                              disabled={actionLoading}
                            >
                              <DispenseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPrescriptions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Empty State */}
      {filteredPrescriptions.length === 0 && (
        <Card sx={{ mt: 3, textAlign: 'center', py: 4 }}>
          <CardContent>
            <MedicalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nu exista prescriptii in perioada selectata
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
              onClick={() => navigate('/clinic/patients')}
            >
              Deschide fișa pacienților
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Vizualizare Prescriptie</DialogTitle>
        <DialogContent>
          {selectedPrescription && (
            <>
              <PrescriptionView
                prescription={selectedPrescription}
                onPrint={() => handlePrintPrescription(selectedPrescription)}
              />
              {viewDetail?.addendums && viewDetail.addendums.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Addendum-uri
                  </Typography>
                  {viewDetail.addendums.map((ad) => (
                    <Typography key={ad.id} variant="body2" sx={{ mb: 1 }}>
                      <strong>{ad.client_visible ? '' : '[Intern] '}</strong>
                      {ad.body}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addendumOpen} onClose={() => !actionLoading && setAddendumOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adaugă addendum</DialogTitle>
        <DialogContent>
          {actionError && addendumOpen && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nu modifică liniile sau cantitările; este înregistrat în istoric și poate fi marcat vizibil pentru
            proprietar.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Text addendum"
            value={addendumText}
            onChange={(e) => setAddendumText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={addendumClientVisible}
                onChange={(e) => setAddendumClientVisible(e.target.checked)}
              />
            }
            label="Vizibil pentru proprietar (client)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddendumOpen(false)} disabled={actionLoading}>
            Renunță
          </Button>
          <Button variant="contained" onClick={() => void submitAddendum()} disabled={actionLoading}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revokeOpen} onClose={() => !actionLoading && setRevokeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Revocare prescripție</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Motivul este obligatoriu (minim 3 caractere). Nu se poate revoca după eliberări înregistrate.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Motiv"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeOpen(false)} disabled={actionLoading}>
            Renunță
          </Button>
          <Button variant="contained" color="error" onClick={() => void submitRevoke()} disabled={actionLoading}>
            Revocă
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PrescriptionsManagementNew;
